import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Camera, Lock, Mail, Phone, Shield, CheckCircle, XCircle, Eye, EyeOff
} from 'lucide-react';
import './styles/customer-profile.css';

const CustomerProfile = () => {
  const [profile, setProfile] = useState({
    firstName: '', lastName: '', email: '', phone: '', profileImage: ''
  });
  const [passwords, setPasswords] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });
  const [pwdReveal, setPwdReveal] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const fileInputRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch('/api/customer/profile', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProfile({
          firstName:    data.customer?.firstName    || '',
          lastName:     data.customer?.lastName     || '',
          email:        data.customer?.email        || '',
          phone:        data.customer?.phone        || '',
          profileImage: data.customer?.profileImage || ''
        });
      } else {
        throw new Error('Failed to fetch profile');
      }
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  };

  const togglePwd = (field) => {
    setPwdReveal(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('profileImage', file);
      const response = await fetch('/api/customer/profile/image', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, profileImage: data.imageUrl }));
        setSuccess('Profile image updated');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/api/customer/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName:  profile.lastName,
          phone:     profile.phone
        })
      });
      if (response.ok) {
        setSuccess('Profile updated');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (passwords.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    setChangingPassword(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/api/customer/profile/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword:     passwords.newPassword
        })
      });
      if (response.ok) {
        setSuccess('Password changed');
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordSection(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update password');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="cprof">
        <div className="cprof__skel cprof__skel--head" />
        <div className="cprof__skel cprof__skel--block" />
        <div className="cprof__skel cprof__skel--block" />
      </div>
    );
  }

  const initial = profile.firstName?.charAt(0)?.toUpperCase()
    || profile.email?.charAt(0)?.toUpperCase()
    || 'U';

  return (
    <div className="cprof">
      <header className="cprof__head">
        <h1 className="cprof__title">Profile</h1>
        <p className="cprof__sub">Manage your personal info and password.</p>
      </header>

      {error && (
        <div className="cprof__alert cprof__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="cprof__alert cprof__alert--success" role="status">
          <CheckCircle size={14} aria-hidden /> <span>{success}</span>
        </div>
      )}

      {/* Avatar */}
      <section className="cprof__card">
        <header className="cprof__card-head">
          <h2 className="cprof__card-title">Profile photo</h2>
        </header>
        <div className="cprof__avatar">
          <div className="cprof__avatar-wrap">
            {profile.profileImage ? (
              <img src={profile.profileImage} alt="Profile" className="cprof__avatar-img" />
            ) : (
              <span className="cprof__avatar-fallback">{initial}</span>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="cprof__avatar-btn"
              aria-label="Upload new photo"
            >
              <Camera size={14} aria-hidden />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              hidden
            />
          </div>
          <div className="cprof__avatar-meta">
            <div className="cprof__avatar-hint">JPG, PNG or GIF.</div>
            <div className="cprof__avatar-sub">Max 5MB. {uploading && 'Uploading…'}</div>
          </div>
        </div>
      </section>

      {/* Personal info */}
      <section className="cprof__card">
        <header className="cprof__card-head">
          <h2 className="cprof__card-title">Personal information</h2>
        </header>
        <form onSubmit={handleSubmit} className="cprof__form">
          <div className="cprof__row">
            <label className="cprof__field">
              <span className="cprof__label">First name</span>
              <input
                type="text"
                name="firstName"
                value={profile.firstName}
                onChange={handleInputChange}
                required
                className="cprof__input"
              />
            </label>
            <label className="cprof__field">
              <span className="cprof__label">Last name</span>
              <input
                type="text"
                name="lastName"
                value={profile.lastName}
                onChange={handleInputChange}
                required
                className="cprof__input"
              />
            </label>
          </div>

          <div className="cprof__row">
            <label className="cprof__field">
              <span className="cprof__label">Email</span>
              <span className="cprof__input-wrap">
                <Mail size={14} className="cprof__input-icon" aria-hidden />
                <input
                  type="email"
                  name="email"
                  value={profile.email}
                  disabled
                  className="cprof__input cprof__input--with-icon"
                />
              </span>
              <span className="cprof__hint">Email cannot be changed.</span>
            </label>
            <label className="cprof__field">
              <span className="cprof__label">Phone</span>
              <span className="cprof__input-wrap">
                <Phone size={14} className="cprof__input-icon" aria-hidden />
                <input
                  type="tel"
                  name="phone"
                  value={profile.phone}
                  onChange={handleInputChange}
                  required
                  className="cprof__input cprof__input--with-icon"
                />
              </span>
            </label>
          </div>

          <div className="cprof__actions">
            <button
              type="submit"
              disabled={saving}
              className="cprof__btn cprof__btn--primary"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </section>

      {/* Password */}
      <section className="cprof__card">
        <header className="cprof__card-head cprof__card-head--split">
          <div>
            <h2 className="cprof__card-title">
              <Shield size={14} aria-hidden /> Password & security
            </h2>
            <p className="cprof__card-sub">Update your password to keep your account secure.</p>
          </div>
          {!showPasswordSection && (
            <button
              type="button"
              onClick={() => setShowPasswordSection(true)}
              className="cprof__btn cprof__btn--ghost cprof__btn--sm"
            >
              <Lock size={14} aria-hidden /> Change
            </button>
          )}
        </header>

        {showPasswordSection && (
          <form onSubmit={handlePasswordUpdate} className="cprof__form">
            <label className="cprof__field">
              <span className="cprof__label">Current password</span>
              <span className="cprof__input-wrap">
                <input
                  type={pwdReveal.current ? 'text' : 'password'}
                  name="currentPassword"
                  value={passwords.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  className="cprof__input cprof__input--with-reveal"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="cprof__reveal"
                  onClick={() => togglePwd('current')}
                  aria-label={pwdReveal.current ? 'Hide password' : 'Show password'}
                >
                  {pwdReveal.current ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </span>
            </label>

            <label className="cprof__field">
              <span className="cprof__label">New password</span>
              <span className="cprof__input-wrap">
                <input
                  type={pwdReveal.next ? 'text' : 'password'}
                  name="newPassword"
                  value={passwords.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={8}
                  className="cprof__input cprof__input--with-reveal"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="cprof__reveal"
                  onClick={() => togglePwd('next')}
                  aria-label={pwdReveal.next ? 'Hide password' : 'Show password'}
                >
                  {pwdReveal.next ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </span>
              <span className="cprof__hint">Must be at least 8 characters.</span>
            </label>

            <label className="cprof__field">
              <span className="cprof__label">Confirm new password</span>
              <span className="cprof__input-wrap">
                <input
                  type={pwdReveal.confirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={passwords.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={8}
                  className="cprof__input cprof__input--with-reveal"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="cprof__reveal"
                  onClick={() => togglePwd('confirm')}
                  aria-label={pwdReveal.confirm ? 'Hide password' : 'Show password'}
                >
                  {pwdReveal.confirm ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </span>
            </label>

            <div className="cprof__actions cprof__actions--split">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordSection(false);
                  setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPwdReveal({ current: false, next: false, confirm: false });
                }}
                className="cprof__btn cprof__btn--ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={changingPassword}
                className="cprof__btn cprof__btn--primary"
              >
                {changingPassword ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default CustomerProfile;
